// frontend/src/pages/QuickBookingPage.tsx
// ✅ FIXED: Updated to use refactored API hooks and fix TypeScript errors

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Loader2,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';

import { 
  BookingAuthStep, 
  QuickBookingForm, 
  ServiceHeader 
} from '@/components/QuickBooking';

// ✅ FIXED: Import from refactored API hooks
import { 
  useGetServiceByCode, 
  useSubmitQuickBooking 
} from '@/hooks/useQuickBookings';
import { convertBookingFormToApiRequest } from '@/utils/bookingDataConverter';

// ✅ FIXED: Import proper types from the service
import type { 
  ServiceDetails as QuickBookingServiceDetails,
  LegacyBookingFormData as BookingFormData,
  QuickBookingRequest 
} from '@/types/quickBooking';

const QuickBookingPage: React.FC = () => {
  const { serviceCode } = useParams<{ serviceCode: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  // State management
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<'auth' | 'booking'>(
    isAuthenticated ? 'booking' : 'auth'
  );

  // ✅ FIXED: Use refactored API hooks
  const { 
    data: service, 
    isLoading: loading, 
    error: serviceError 
  } = useGetServiceByCode(serviceCode || '');

  const submitBookingMutation = useSubmitQuickBooking();

  // Get the step number for QuickBookingLayout progress
  const getProgressStep = () => {
    if (currentStep === 'auth') return 1;
    if (currentStep === 'booking') return 2;
    return 3; // success (handled by separate page)
  };

  // Update step based on auth status
  useEffect(() => {
    setCurrentStep(isAuthenticated ? 'booking' : 'auth');
  }, [isAuthenticated]);

  // Handle auth success
  const handleAuthSuccess = () => {
    setCurrentStep('booking');
  };

  // ✅ FIXED: Handle form submission with refactored API
  const handleBookingSubmit = async (formData: BookingFormData) => {
    if (!service) return;

    setSubmitting(true);
    setError(null);

    try {
      // Ensure user is authenticated
      if (!isAuthenticated) {
        throw new Error('Authentication required. Please log in first.');
      }

      // ✅ FIXED: Convert form data to API request format
      const bookingRequest: QuickBookingRequest = {
        provider_service_id: service.provider_service_id,
        provider_id: service.provider_id,
        service_code: service.service_code,
        customer_name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone_number: formData.phoneNumber,
        service_date: formData.serviceDate,
        preferred_time: formData.preferredTime,
        // Construct location from address components
        location: [
          formData.address.addressLine1,
          formData.address.addressLine2,
          formData.address.landmark ? `Near ${formData.address.landmark}` : '',
          formData.address.city,
          formData.address.district,
          formData.address.state
        ].filter(Boolean).join(', '),
        pincode: formData.address.pincode,
        description: formData.description || '',
        contact_preference: formData.contactPreference || 'phone',
        urgency: formData.urgency || 'normal',
        // Include coordinates if available
        latitude: formData.address.latitude,
        longitude: formData.address.longitude
      };

      // ✅ FIXED: Submit booking using the mutation
      const response = await submitBookingMutation.mutateAsync(bookingRequest);

      // ✅ FIXED: Handle successful response - redirect to success page
      if (response) {
        const bookingId = response.booking?.booking_id || response.id || 'unknown';
        const requestId = response.request?.id || response.request_id || 'unknown';
        
        navigate(`/booking-success?booking_id=${bookingId}&request_id=${requestId}`, {
          replace: true
        });
      }

    } catch (err: any) {
      console.error('Booking submission error:', err);
      setError(err.message || 'Failed to submit booking request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle auth errors
  const handleAuthError = (errorMessage: string) => {
    setError(errorMessage);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading service details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (serviceError || !service) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Service Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            {serviceError?.message || 'The requested service could not be found or is no longer available.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md flex items-center mx-auto"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Global error display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Authentication Step */}
      {currentStep === 'auth' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <BookingAuthStep
            onSuccess={handleAuthSuccess}
            onError={handleAuthError}
          />
        </div>
      )}

      {/* Booking Form Step */}
      {currentStep === 'booking' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <QuickBookingForm
            service={service as QuickBookingServiceDetails} // ✅ FIXED: Cast to correct type
            onSubmit={handleBookingSubmit}
            isSubmitting={submitting}
            error={error}
          />
        </div>
      )}
    </div>
  );
};

export default QuickBookingPage;